import styled, { css } from 'styled-components'

import type { Align } from './AnalyticsTable.types'

export const Container = styled.div`
  position: relative;
  width: 100%;
  border-radius: var(--radius-brand);
  border: 1px solid var(--border-secondary);
  background: var(--bg-primary);
`

export const TableWrapper = styled.div<{ maxHeight?: number }>`
  position: relative;
  overflow: auto;
  max-height: ${({ maxHeight }) => (maxHeight ? `${maxHeight}px` : 'unset')};
  -webkit-overflow-scrolling: touch;
  border-radius: 3px;
`

export const ShadowsLayer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 6;
`

export const Table = styled.div`
  display: table;
  border-collapse: separate;
  border-spacing: 0;
  width: max-content;
  min-width: 100%;

  color: var(--text-primary);
  /* body/md/regular */
  font-family: var(--font-family-body);
  font-size: var(--font-size-400);
  font-style: normal;
  font-weight: var(--font-weight-regular);
  line-height: var(--font-line-height-400); /* 150% */
  letter-spacing: var(--font-letter-spacing-normal);
`

export const baseCell = css<{ align?: Align }>`
  display: table-cell;
  width: fit-content;
  white-space: nowrap;
  vertical-align: middle;
  text-align: ${({ align }) => align ?? 'left'};
  background: var(--bg-primary);
  padding: var(--space-200) var(--space-400);

  &:last-child {
    padding-right: var(--space-600);
  }
`

export const Td = styled.div<{ stickyLeft?: boolean; align?: Align }>`
  ${baseCell};

  ${({ stickyLeft }) =>
    stickyLeft &&
    css`
      position: sticky;
      left: 0;
      z-index: 2;
    `}
`

export const Row = styled.div<{ sticky?: boolean; head?: boolean }>`
  display: table-row;
  padding: 0 var(--space-400) 0 var(--space-200);

  &:not(:last-child) ${Td} {
    border-bottom: 1px solid var(--border-secondary);
  }

  &:hover ${Td} {
    background: var(--bg-secondary);
  }

  ${({ sticky, head }) =>
    sticky &&
    css`
      position: sticky;
      top: ${head ? 0 : 41}px;
      z-index: 3;
      overflow: hidden;
    `}
`

export const ShadowLeft = styled.div<{ visible: boolean }>`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 28px;
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  transition: opacity 0.15s ease;
  background: linear-gradient(270deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.1) 100%);
`

export const ShadowRight = styled.div<{ visible: boolean }>`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  width: 28px;
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  transition: opacity 0.15s ease;
  background: linear-gradient(90deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.1) 100%);

  border-radius: 0 3px 3px 0;
`

export const Sorting = styled.div<{ visible: boolean }>`
  position: absolute;
  display: none;
  top: calc(50% - 7px);
  left: calc(100%);
  padding-left: var(--space-150);
  flex-direction: column;
  color: var(--icon-primary);
  z-index: 1;

  ${({ visible }) =>
    visible &&
    css`
      display: flex;
    `};
`

export const ActiveIcon = styled.div<{ visible: boolean }>`
  position: absolute;
  display: none;
  top: calc(50% - 4px);
  right: calc(100%);
  padding-right: var(--space-150);
  color: var(--icon-primary);
  z-index: 1;

  ${({ visible }) =>
    visible &&
    css`
      display: block;
    `};
`

export const SortingIcon = styled.div<{ isActive: boolean }>`
  opacity: 0.3;

  ${({ isActive }) =>
    isActive &&
    css`
      opacity: 1;
    `}
`

export const HeadRowLabel = styled.div<{ underline?: boolean }>`
  display: inline-block;
  font-family: var(--font-family-body);
  font-size: var(--font-size-300);
  font-style: normal;
  font-weight: var(--font-weight-regular);
  line-height: var(--font-line-height-300); /* 142.857% */
  letter-spacing: var(--font-letter-spacing-loose);
  position: relative;
  cursor: pointer;
  user-select: none;

  ${({ underline }) =>
    underline &&
    css`
      text-decoration-line: underline;
      text-decoration-style: dotted;
      text-decoration-skip-ink: auto;
      text-decoration-thickness: 10%; /* 1.4px */
      text-underline-offset: 25%; /* 3.5px */
    `}

  &:hover {
    color: var(--text-primary);

    ${Sorting} {
      display: flex;
    }
  }
`

export const Th = styled.div<{ stickyLeft?: boolean; align?: Align }>`
  ${baseCell};
  position: sticky;
  top: 0;
  z-index: 4;
  color: var(--text-secondary);
  background: var(--bg-secondary);
  border-bottom: 1px solid var(--border-secondary);

  ${({ align }) =>
    align === 'right' &&
    css`
      ${HeadRowLabel} {
        width: auto;
      }
    `}

  ${({ stickyLeft }) =>
    stickyLeft &&
    css`
      position: sticky;
      left: 0;
      z-index: 6;
    `}

  &:hover {
    z-index: 5;
  }
`

export const TooltipWrapper = styled.div`
  display: inline-block;
  height: 20px;

  .tooltip-container,
  .tooltip-reference {
    display: inline-block;
    height: 20px;
  }
`

export const EmptyDataCell = styled.td`
  display: table-cell;
  text-align: center;
  vertical-align: middle;
  background: var(--bg-primary);
  padding: var(--space-500);
  color: var(--text-secondary);
  font-size: var(--font-size-300);
  line-height: var(--font-line-height-300); /* 150% */
  letter-spacing: var(--font-letter-spacing-normal);
`

export const ExpandButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-100);
  margin-right: var(--space-200);
  cursor: pointer;
  border-radius: var(--radius-brand);
  color: var(--text-secondary);
  transition: all 0.15s ease;

  &:hover {
    background: var(--bg-tertiary);
    color: var(--text-primary);
  }
`
